import React, { useState, useEffect, useRef } from "react";
import { API_BASE_URL, COUNTRY_CODES } from "./constants/adminConstants";
import PasswordStrengthBar from "./PasswordStrengthBar";
import "./css/adminStyles.css";

function AdminProfilePanel({ user, onClose }) {
  const [activeTab, setActiveTab] = useState("profile");
  const [firstName, setFirstName] = useState(user.firstName || "");
  const [lastName, setLastName] = useState(user.lastName || "");
  const [countryCode, setCountryCode] = useState(user.countryCode || "+1");
  const [email, setEmail] = useState(user.email || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [success, setSuccess] = useState("");
  const [editing, setEditing] = useState(false);

  const [otpStep, setOtpStep] = useState(null);
  const [otpPurpose, setOtpPurpose] = useState(null);
  const [method, setMethod] = useState("email");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [confirmSwitch, setConfirmSwitch] = useState(null);
  const intervalRef = useRef(null);
  const inputRefs = useRef([]);

  const rolePath = user?.role?.toLowerCase() || "admin";

  useEffect(() => {
    if (timer > 0) {
      intervalRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [timer]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const [checkingPassword, setCheckingPassword] = useState(false);

  const handlePasswordContinue = async () => {
    setError("");
    setPasswordError("");
    if (!password) { setError("Enter a new password."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }

    setCheckingPassword(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/check-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id, password }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.same) {
          setPasswordError("New password must be different from current password.");
          setCheckingPassword(false);
          return;
        }
      }
    } catch {
    }

    setCheckingPassword(false);
    setOtpPurpose("password");
    setMethod("email");
    setOtpStep("choose");
  };

  const handleSendOtp = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id, method }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Failed to send OTP."); setLoading(false); return; }
      setOtpSent(true);
      setOtpStep("otp");
      setTimer(120);
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch { setError("Server error."); }
    finally { setLoading(false); }
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newOtp = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i++) newOtp[i] = pasted[i];
    setOtp(newOtp);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerifyOtp = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id, otp: otp.join("") }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Verification failed."); setLoading(false); return; }

      if (otpPurpose === "password") {
        const updateRes = await fetch(`${API_BASE_URL}/auth/reset-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: user.id, password }),
        });
        const updateData = await updateRes.json();
        if (!updateRes.ok) {
          if (updateData.error?.toLowerCase().includes("different from current")) {
            setPasswordError(updateData.error);
          } else {
            setError(updateData.error || "Failed to update password.");
          }
          setLoading(false); return;
        }
        setSuccess("Password updated successfully.");
        setPassword("");
        setConfirmPassword("");
      } else if (otpPurpose === "profile") {
        const submitRes = await fetch(`${API_BASE_URL}/${rolePath}/profile-change`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${sessionStorage.getItem("tvc_token")}` },
          body: JSON.stringify({
            userId: user.id,
            firstName, lastName, email, phone, countryCode,
          }),
        });
        const submitData = await submitRes.json();
        if (!submitRes.ok) { setError(submitData.error || "Failed to submit profile change."); setLoading(false); return; }
        setSuccess("Profile change submitted. Awaiting admin approval.");
        setEditing(false);
      }

      setActiveTab("profile");
      setOtpStep(null);
      setOtpPurpose(null);
      setOtpSent(false);
    } catch { setError("Server error."); }
    finally { setLoading(false); }
  };

  const handleProfileEdit = () => { setEditing(true); setError(""); setSuccess(""); };

  const handleProfileCancel = () => {
    setEditing(false);
    setFirstName(user.firstName || "");
    setLastName(user.lastName || "");
    setEmail(user.email || "");
    setPhone(user.phone || "");
    setCountryCode(user.countryCode || "+1");
  };

  const handleProfileSave = () => {
    setError("");
    if (!firstName || !lastName) { setError("First and last name are required."); return; }
    if (!email) { setError("Email is required."); return; }
    setActiveTab("otp");
    setOtpPurpose("profile");
    setMethod("email");
    setOtpStep("choose");
  };

  const handleResend = () => { setTimer(0); handleSendOtp(); };

  const handleMethodSwitch = (newMethod) => {
    if (otpSent && newMethod !== method) setConfirmSwitch(newMethod);
    else setMethod(newMethod);
  };

  const confirmMethodSwitch = () => {
    setMethod(confirmSwitch);
    setOtpSent(false);
    setOtp(["", "", "", "", "", ""]);
    setTimer(0);
    setOtpStep("choose");
    setError("");
    setConfirmSwitch(null);
  };

  return (
    <div className="AdminCard">
      <div className="AdminProfilePanel">
        <div className="AdminCardHeader">
          {activeTab !== "profile" && (
            <button
              className="AdminBackArrow"
              onClick={() => { setActiveTab("profile"); setOtpPurpose(null); setOtpStep(null); setOtpSent(false); setPasswordError(""); }}
              type="button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></svg>
            </button>
          )}
          <h3 style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 20, fontWeight: 700, color: "#1B2A4A" }}>
            {activeTab === "profile" ? "Profile Information" : activeTab === "password" ? "Update Password" : otpPurpose === "profile" ? "Verify to Update Profile" : "Verify to Update Password"}
          </h3>

        </div>

        {activeTab === "profile" && (
          <>
            {editing ? (
              <>
                <div className="AdminFormGroup">
                  <label className="AdminLabel">User ID</label>
                  <div className="AdminProfileUserId">{user.loginId}</div>
                </div>
                <div className="AdminRow">
                  <div className="AdminFormGroup">
                    <label className="AdminLabel">First Name</label>
                    <input className="AdminInput" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div className="AdminFormGroup">
                    <label className="AdminLabel">Last Name</label>
                    <input className="AdminInput" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </div>
                </div>
                <div className="AdminRow">
                  <div className="AdminFormGroup">
                    <label className="AdminLabel">Email</label>
                    <input className="AdminInput" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                  <div className="AdminFormGroup">
                    <label className="AdminLabel">Phone</label>
                    <div className="AdminCountryCodeRow">
                      <div className="AdminCountryCodeSelect">
                        <div className="AdminSelect AdminCountryCodeDisplay">{countryCode}</div>
                        <select className="AdminSelect AdminCountryCodeNative" value={countryCode} onChange={(e) => setCountryCode(e.target.value)}>
                          {COUNTRY_CODES.map((c) => (
                            <option key={c.value + c.label} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="AdminPhoneInput">
                        <input className="AdminInput" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="AdminRow">
                  <div className="AdminFormGroup">
                    <label className="AdminLabel">Full Name</label>
                    <div className="AdminProfileDisplay">{firstName} {lastName}</div>
                  </div>
                  <div className="AdminFormGroup">
                    <label className="AdminLabel">User ID</label>
                    <div className="AdminProfileUserId">{user.loginId}</div>
                  </div>
                </div>
                <div className="AdminRow">
                  <div className="AdminFormGroup">
                    <label className="AdminLabel">Email</label>
                    <div className="AdminProfileDisplay">{email}</div>
                  </div>
                  <div className="AdminFormGroup">
                    <label className="AdminLabel">Phone</label>
                    <div className="AdminProfileDisplay">{phone ? `${countryCode} ${phone}` : "-"}</div>
                  </div>
                </div>
              </>
            )}

            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              {editing ? (
                <>
                  <button className="AdminButton AdminButtonSmall AdminButtonSuccess" onClick={handleProfileSave}>
                    Save
                  </button>
                  <button className="AdminButton AdminButtonSmall AdminButtonDanger" onClick={handleProfileCancel}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button className="AdminButton AdminButtonSmall" onClick={handleProfileEdit}>
                    Edit
                  </button>
                  <button className="AdminButton AdminButtonSmall AdminButtonSecondary" onClick={() => setActiveTab("password")}>
                    Update Password
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {activeTab === "password" && (
          <>

            <div className="AdminRow">
              <div className="AdminFormGroup">
                <label className="AdminLabel">New Password</label>
                <div className="AdminPwInputWrap">
                  <input
                    className="AdminInput AdminPwInput"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
                  />
                  <button type="button" className="AdminPwToggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    )}
                  </button>
                </div>
                <PasswordStrengthBar password={password} />
                {passwordError && <p className="AdminFieldError" style={{ marginTop: 4 }}>{passwordError}</p>}
              </div>
              <div className="AdminFormGroup">
                <label className="AdminLabel">Confirm Password</label>
                <div className="AdminPwInputWrap">
                  <input
                    className={`AdminInput AdminPwInput ${confirmPassword && password !== confirmPassword ? "AdminInputError" : ""}`}
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button type="button" className="AdminPwToggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)} tabIndex={-1}>
                    {showConfirmPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    )}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="AdminFieldError">Passwords do not match.</p>
                )}
              </div>
            </div>

            {!otpPurpose && (
              <button className="AdminButton" style={{ marginTop: 16, width: "fit-content", padding: "8px 24px" }} onClick={handlePasswordContinue} disabled={checkingPassword || !password || !confirmPassword || password !== confirmPassword || !!passwordError}>
                {checkingPassword ? "Checking..." : "Continue"}
              </button>
            )}

            {otpPurpose === "password" && (
              <div style={{ marginTop: 20 }}>
                <div className="AdminOtpMethodToggle">
                  <button className={`AdminToggleBtn ${method === "email" ? "AdminToggleBtnActive" : ""}`} onClick={() => handleMethodSwitch("email")}>Verify via Email</button>
                  <button className={`AdminToggleBtn ${method === "phone" ? "AdminToggleBtnActive" : ""}`} onClick={() => handleMethodSwitch("phone")}>Verify via Phone</button>
                </div>

                {confirmSwitch && (
                  <div className="AdminOtpSwitchConfirm" style={{ position: "relative" }}>
                    <button className="AdminOtpSwitchClose" onClick={() => setConfirmSwitch(null)} type="button">&times;</button>
                    <p className="AdminOtpSwitchText">The current OTP will not be valid for the new method.</p>
                    <div className="AdminOtpSwitchActions">
                      <button className="AdminButton AdminButtonSmall" onClick={confirmMethodSwitch}>Switch</button>
                    </div>
                  </div>
                )}

            {otpStep === "choose" && (
              <div style={{ textAlign: "center" }}>
                <button className="AdminButton AdminButtonSecondary AdminButtonSmall" onClick={handleSendOtp} disabled={loading}>
                  {loading ? "Sending..." : `Send OTP to ${method}`}
                </button>
              </div>
            )}

            {otpStep === "otp" && otpSent && (
              <>
                <p className="AdminProfileOtpSent" style={{ textAlign: "center", marginBottom: 8 }}>OTP sent to {method === "email" ? user.email : `${user.countryCode} ${user.phone}`}</p>
                <div className="AdminOtpBoxRow">
                      {otp.map((digit, i) => (
                        <input
                          key={i}
                          ref={(el) => (inputRefs.current[i] = el)}
                          className="AdminOtpBox"
                          type="text"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(i, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(i, e)}
                          onPaste={i === 0 ? handleOtpPaste : undefined}
                          autoFocus={i === 0}
                        />
                      ))}
                    </div>
                    {timer > 0 && <p className="AdminOtpTimer" style={{ textAlign: "center", width: "100%" }}>{formatTime(timer)}</p>}
                    <div className="AdminOtpBtnGroup">
                      {timer === 0 && <button className="AdminButton AdminButtonSecondary AdminButtonSmall" onClick={handleResend} disabled={loading}>Resend</button>}
                      <button className="AdminButton AdminButtonSmall" onClick={handleVerifyOtp} disabled={loading || otp.join("").length !== 6}>
                        {loading ? "Verifying..." : "Verify OTP"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === "otp" && otpPurpose === "profile" && (
          <div>
            <div className="AdminOtpMethodToggle">
              <button className={`AdminToggleBtn ${method === "email" ? "AdminToggleBtnActive" : ""}`} onClick={() => handleMethodSwitch("email")}>Verify via Email</button>
              <button className={`AdminToggleBtn ${method === "phone" ? "AdminToggleBtnActive" : ""}`} onClick={() => handleMethodSwitch("phone")}>Verify via Phone</button>
            </div>

            {confirmSwitch && (
              <div className="AdminOtpSwitchConfirm" style={{ position: "relative" }}>
                <button className="AdminOtpSwitchClose" onClick={() => setConfirmSwitch(null)} type="button">&times;</button>
                <p className="AdminOtpSwitchText">The current OTP will not be valid for the new method.</p>
                <div className="AdminOtpSwitchActions">
                  <button className="AdminButton AdminButtonSmall" onClick={confirmMethodSwitch}>Switch</button>
                </div>
              </div>
            )}

            {otpStep === "choose" && (
              <div style={{ textAlign: "center" }}>
                <button className="AdminButton AdminButtonSecondary AdminButtonSmall" onClick={handleSendOtp} disabled={loading}>
                  {loading ? "Sending..." : `Send OTP to ${method}`}
                </button>
              </div>
            )}

            {otpStep === "otp" && otpSent && (
              <>
                <p className="AdminProfileOtpSent" style={{ textAlign: "center", marginBottom: 16 }}>OTP sent to {method === "email" ? user.email : `${user.countryCode} ${user.phone}`}</p>
                <div className="AdminOtpBoxRow">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (inputRefs.current[i] = el)}
                      className="AdminOtpBox"
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      onPaste={i === 0 ? handleOtpPaste : undefined}
                      autoFocus={i === 0}
                    />
                  ))}
                </div>
                {timer > 0 && <p className="AdminOtpTimer" style={{ textAlign: "center", width: "100%" }}>{formatTime(timer)}</p>}
                <div className="AdminOtpBtnGroup">
                  {timer === 0 && <button className="AdminButton AdminButtonSecondary AdminButtonSmall" onClick={handleResend} disabled={loading}>Resend</button>}
                  <button className="AdminButton AdminButtonSmall" onClick={handleVerifyOtp} disabled={loading || otp.join("").length !== 6}>
                    {loading ? "Verifying..." : "Verify OTP"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {error && <p className="AdminError" style={{ marginTop: 12 }}>{error}</p>}
        {success && <p className="AdminSuccess" style={{ marginTop: 12 }}>{success}</p>}
      </div>
    </div>
  );
}

export default AdminProfilePanel;
